/**
 * Vocabulary Admin Manager
 * 
 * P1 VOCABULARY MANAGEMENT:
 * - Manages tenant-scoped Channels, Verticals, and Platforms
 * - Keys are immutable after creation
 * - Version increments on label change
 * - Archived entries remain for historical data
 */

import React, { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { useTenant } from "@/contexts/TenantContext";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { 
  Plus, 
  MoreHorizontal, 
  Edit2, 
  Archive, 
  ArrowUp, 
  ArrowDown,
  RefreshCw,
  Hash,
  Tag,
  Layers,
} from "lucide-react";

// Vocabulary type configuration
const VOCAB_TYPES = {
  channels: {
    label: "Channels",
    description: "Traffic channels for lead classification (e.g., Paid Traffic, Referral Traffic)",
    icon: Hash,
    examples: "paid_traffic, referral_traffic, organic_traffic",
  },
  verticals: {
    label: "Verticals",
    description: "Product verticals for lead routing (e.g., Mortgage Protection, Final Expense)",
    icon: Layers,
    examples: "mortgage_protection, final_expense, safe_money",
  },
  platforms: {
    label: "Platforms",
    description: "Source platforms for lead attribution (e.g., Meta, Google)",
    icon: Tag,
    examples: "meta, google, ghl, referral_partner",
  },
};

export default function VocabularyManager() {
  const { selectedTenantId } = useTenant();
  const { toast } = useToast();
  
  const [activeTab, setActiveTab] = useState("channels");
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [formData, setFormData] = useState({ key: "", label: "", sort_order: 0 });
  const [submitting, setSubmitting] = useState(false);

  // Fetch vocabulary entries
  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const params = { include_archived: showArchived };
      const res = await api.get(`/admin/vocabulary/${activeTab}`, { params });
      setEntries(res.data.entries || []);
    } catch (err) {
      toast({
        title: "Failed to load vocabulary",
        description: err.response?.data?.detail || "Please try again",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [activeTab, showArchived, toast]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries, selectedTenantId]);

  // Open create dialog
  const openCreateDialog = () => {
    setEditingEntry(null);
    const maxOrder = entries.length > 0 
      ? Math.max(...entries.map(e => e.sort_order)) + 1 
      : 0;
    setFormData({ key: "", label: "", sort_order: maxOrder });
    setDialogOpen(true);
  };

  // Open edit dialog
  const openEditDialog = (entry) => {
    setEditingEntry(entry);
    setFormData({
      key: entry.key,
      label: entry.label,
      sort_order: entry.sort_order,
    });
    setDialogOpen(true);
  };

  // Handle form submit
  const handleSubmit = async () => {
    if (!formData.label.trim()) {
      toast({ title: "Label is required", variant: "destructive" });
      return;
    }
    
    if (!editingEntry && !formData.key.trim()) {
      toast({ title: "Key is required", variant: "destructive" });
      return;
    }

    setSubmitting(true);
    try {
      if (editingEntry) {
        // Update existing entry
        await api.patch(`/admin/vocabulary/${activeTab}/${editingEntry.id}`, {
          label: formData.label.trim(),
          sort_order: formData.sort_order,
        });
        toast({ title: "Entry updated" });
      } else {
        // Create new entry
        await api.post(`/admin/vocabulary/${activeTab}`, {
          key: formData.key.trim().toLowerCase().replace(/\s+/g, "_").replace(/-/g, "_"),
          label: formData.label.trim(),
          sort_order: formData.sort_order,
          is_active: true,
        });
        toast({ title: "Entry created" });
      }
      
      setDialogOpen(false);
      fetchEntries();
    } catch (err) {
      toast({
        title: editingEntry ? "Failed to update" : "Failed to create",
        description: err.response?.data?.detail || "Please try again",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Toggle active status
  const toggleActive = async (entry) => {
    try {
      await api.patch(`/admin/vocabulary/${activeTab}/${entry.id}`, {
        is_active: !entry.is_active,
      });
      toast({ 
        title: entry.is_active ? "Entry archived" : "Entry activated",
      });
      fetchEntries();
    } catch (err) {
      toast({
        title: "Failed to update status",
        description: err.response?.data?.detail || "Please try again",
        variant: "destructive",
      });
    }
  };

  // Move entry up/down in sort order
  const moveEntry = async (entry, direction) => {
    const newOrder = direction === "up" 
      ? entry.sort_order - 1 
      : entry.sort_order + 1;
    
    if (newOrder < 0) return;
    
    try {
      await api.patch(`/admin/vocabulary/${activeTab}/${entry.id}`, {
        sort_order: newOrder,
      });
      fetchEntries();
    } catch (err) {
      toast({
        title: "Failed to reorder",
        variant: "destructive",
      });
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString();
  };

  const config = VOCAB_TYPES[activeTab];
  const Icon = config?.icon || Hash;

  return (
    <Card className="bg-white/5 border-white/10">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-xl text-white flex items-center gap-2">
              <Icon className="h-5 w-5 text-indigo-400" />
              Vocabulary Management
            </CardTitle>
            <CardDescription className="text-slate-400 mt-1">
              Manage controlled vocabularies for classification fields
            </CardDescription>
          </div>
          <Button
            onClick={fetchEntries}
            variant="ghost"
            size="sm"
            data-testid="vocab-refresh-btn"
          >
            <RefreshCw className="h-4 w-4 mr-1" />
            Refresh
          </Button>
        </div>
      </CardHeader>
      
      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-white/5 border-white/10 mb-4">
            {Object.entries(VOCAB_TYPES).map(([key, conf]) => {
              const TabIcon = conf.icon;
              return (
                <TabsTrigger
                  key={key}
                  value={key}
                  className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-300"
                  data-testid={`vocab-tab-${key}`}
                >
                  <TabIcon className="h-4 w-4 mr-1.5" />
                  {conf.label}
                </TabsTrigger>
              );
            })}
          </TabsList>

          {Object.keys(VOCAB_TYPES).map((vocabType) => (
            <TabsContent key={vocabType} value={vocabType} className="mt-0">
              <div className="space-y-4">
                {/* Description + Actions */}
                <div className="flex items-center justify-between">
                  <p className="text-sm text-slate-400">
                    {VOCAB_TYPES[vocabType].description}
                  </p>
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 text-sm text-slate-400">
                      <Switch
                        checked={showArchived}
                        onCheckedChange={setShowArchived}
                        className="data-[state=checked]:bg-indigo-500"
                      />
                      Show archived
                    </label>
                    <Button
                      onClick={openCreateDialog}
                      size="sm"
                      data-testid="vocab-create-btn"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Entry
                    </Button>
                  </div>
                </div>

                {/* Table */}
                {loading ? (
                  <div className="text-slate-400 text-center py-8">Loading...</div>
                ) : entries.length === 0 ? (
                  <div className="text-slate-400 text-center py-8">
                    No entries found. Click "Add Entry" to create one.
                  </div>
                ) : (
                  <div className="border border-white/10 rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-white/10">
                          <TableHead className="text-slate-300">Label</TableHead>
                          <TableHead className="text-slate-300">Key</TableHead>
                          <TableHead className="text-slate-300 text-center">Active</TableHead>
                          <TableHead className="text-slate-300 text-center">Order</TableHead>
                          <TableHead className="text-slate-300 text-center">Version</TableHead>
                          <TableHead className="text-slate-300">Replaced By</TableHead>
                          <TableHead className="text-slate-300">Created</TableHead>
                          <TableHead className="text-slate-300 text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {entries.map((entry) => (
                          <TableRow 
                            key={entry.id} 
                            className={`border-white/5 ${!entry.is_active ? 'opacity-50' : ''}`}
                            data-testid={`vocab-row-${entry.key}`}
                          >
                            <TableCell className="text-slate-100 font-medium">
                              {entry.label}
                            </TableCell>
                            <TableCell className="font-mono text-xs text-slate-400">
                              {entry.key}
                            </TableCell>
                            <TableCell className="text-center">
                              {entry.is_active ? (
                                <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30">
                                  Active
                                </Badge>
                              ) : (
                                <Badge variant="secondary" className="bg-slate-500/20 text-slate-400">
                                  Archived
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-center text-slate-300">
                              {entry.sort_order}
                            </TableCell>
                            <TableCell className="text-center text-slate-400">
                              v{entry.version}
                            </TableCell>
                            <TableCell className="text-slate-400 font-mono text-xs">
                              {entry.replaced_by_key || "—"}
                            </TableCell>
                            <TableCell className="text-slate-400 text-xs">
                              {formatDate(entry.created_at)}
                            </TableCell>
                            <TableCell className="text-right">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0 text-slate-400 hover:text-slate-200 hover:bg-white/5"
                                    data-testid={`vocab-actions-${entry.key}`}
                                  >
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="bg-slate-900 border-slate-700">
                                  <DropdownMenuItem onClick={() => openEditDialog(entry)}>
                                    <Edit2 className="h-4 w-4 mr-2" />
                                    Edit Label
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => moveEntry(entry, "up")}>
                                    <ArrowUp className="h-4 w-4 mr-2" />
                                    Move Up
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => moveEntry(entry, "down")}>
                                    <ArrowDown className="h-4 w-4 mr-2" />
                                    Move Down
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => toggleActive(entry)}>
                                    <Archive className="h-4 w-4 mr-2" />
                                    {entry.is_active ? "Archive" : "Activate"}
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            </TabsContent>
          ))}
        </Tabs>

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-slate-900 border-slate-700 text-white">
            <DialogHeader>
              <DialogTitle>
                {editingEntry ? `Edit ${config.label.slice(0, -1)}` : `Add ${config.label.slice(0, -1)}`}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {!editingEntry && (
                <div className="space-y-2">
                  <Label className="text-slate-200">Key</Label>
                  <Input
                    value={formData.key}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      key: e.target.value.toLowerCase().replace(/\s+/g, "_").replace(/-/g, "_")
                    }))}
                    placeholder={`e.g., ${VOCAB_TYPES[activeTab].examples.split(", ")[0]}`}
                    className="bg-white/5 border-white/10 text-slate-100 font-mono"
                    data-testid="vocab-key-input"
                  />
                  <p className="text-xs text-slate-500">
                    Snake_case only. Cannot be changed after creation.
                  </p>
                </div>
              )}
              
              {editingEntry && (
                <div className="space-y-2">
                  <Label className="text-slate-200">Key (immutable)</Label>
                  <Input
                    value={formData.key}
                    disabled
                    className="bg-white/5 border-white/10 text-slate-400 font-mono"
                  />
                </div>
              )}
              
              <div className="space-y-2">
                <Label className="text-slate-200">Label</Label>
                <Input
                  value={formData.label}
                  onChange={(e) => setFormData(prev => ({ ...prev, label: e.target.value }))}
                  placeholder="Human-readable display name"
                  className="bg-white/5 border-white/10 text-slate-100"
                  data-testid="vocab-label-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-slate-200">Sort Order</Label>
                <Input
                  type="number"
                  value={formData.sort_order}
                  onChange={(e) => setFormData(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))}
                  className="bg-white/5 border-white/10 text-slate-100 w-24"
                  data-testid="vocab-order-input"
                />
                <p className="text-xs text-slate-500">
                  Lower numbers appear first in dropdowns.
                </p>
              </div>
            </div>
            
            <DialogFooter>
              <Button
                variant="secondary"
                onClick={() => setDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                data-testid="vocab-submit-btn"
              >
                {submitting ? "Saving..." : (editingEntry ? "Update" : "Create")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
